<template>
  <div :class="['peers-container-wrapper', viewMode, {
    invisible: expandedWhiteboard
  }]">
    <div
      v-show="!expandedWhiteboard"
      class="peers-container"
    >
      <peer-view
        v-if="isDisplayingMyPeerView"
        :avatar="guest ? null : signedInUser.meta.avatar"
        :displayName="myPeer.displayName"
        :videoStream="myPeer.videoStream"
        :micState="myPeer.micState"
        :isActiveSpeaker="myUserId == activeSpeakerPeerId"
        :isRaisedHands="myPeer.raisedHandsState"
        :isScreenSharer="myUserId == screenSharerPeerId"
        myself
        displayIcon
        @onClickRaisedHands="$emit('onClickMyRaisedHands')"
        @onClickMicState="$emit('onClickMyMicState')"
      />
      <peer-view
        v-if="isDisplayingScreenSharerPeerView"
        :avatar="avatarByUserId(screenSharerPeerId)"
        :displayName="peers[screenSharerPeerId].displayName"
        :videoStream="peers[screenSharerPeerId].videoStream"
        :audioStream="peers[screenSharerPeerId].audioStream"
        :micState="peers[screenSharerPeerId].micState"
        :isActiveSpeaker="screenSharerPeerId == activeSpeakerPeerId"
        :isRaisedHands="peers[screenSharerPeerId].raisedHandsState"
        :isScreenSharer="true"
        displayIcon
      />
      <peer-view
        v-for="(peerId) in frontPeerIds"
        :key="peerId"
        :avatar="avatarByUserId(peerId)"
        :displayName="peers[peerId].displayName"
        :videoStream="peers[peerId].videoStream"
        :audioStream="peers[peerId].audioStream"
        :micState="peers[peerId].micState"
        :isActiveSpeaker="peerId == activeSpeakerPeerId"
        :isRaisedHands="peers[peerId].raisedHandsState"
        :isScreenSharer="peerId == screenSharerPeerId"
        displayIcon
      />
      <peers-carousel
        v-show="isDisplayingCarousel"
        :prev="carousel.prev"
        :next="carousel.next"
        :sm="viewMode === 'whiteboard'"
        @onClickPrev="changeCarousel(carousel.current - 1)"
        @onClickNext="changeCarousel(carousel.current + 1)"
      />
    </div>
    <audio
      v-for="(peerId) in backPeerIds"
      :key="peerId"
      :srcObject.prop="peers[peerId].audioStream"
      autoplay
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import User from '@/store/models/User'
import PeerView from '@/components/molecules/room/PeerView'
import PeersCarousel from '@/components/organisms/room/PeersCarousel'

export default {
  name: 'PeersContainer',
  components: {
    PeerView,
    PeersCarousel
  },
  props: {
    viewMode: {
      type: String,
      default: 'fluid'
    },
    guest: Boolean
  },
  computed: {
    avatarByUserId: function() {
      return function(id) {
        return this.guest ?
               this.guestInvitedVideoRoom.users.filter((user) => user.id == id)[0]?.meta?.avatar :
               User.find(id)?.meta?.avatar
      }
    },
    myUserId: function() {
      return this.guest ?
             this.guestInvitedVideoRoom.myMember.userId :
             this.signedInUser.id
    },
    ...mapGetters({
      signedInUser: 'auth/signedInUser',
      myPeer: 'roomPeers/myPeer',
      peers: 'roomPeers/peers',
      activeSpeakerPeerId: 'roomPeers/activeSpeakerPeerId',
      screenSharerPeerId: 'roomPeers/screenSharerPeerId',
      carousel: 'roomPeers/carousel',
      perPages: 'roomPeers/perPages',
      frontPeerIds: 'roomPeers/frontPeerIds',
      backPeerIds: 'roomPeers/backPeerIds',
      expandedWhiteboard: 'roomWhiteboard/expanded',
      guestInvitedVideoRoom: 'inviteAuth/invitedVideoRoom'
    }),
    isDisplayingMyPeerView: function() {
      if (this.$deviceInfo.isMobile) {
        if (this.viewMode === 'fluid') {
          // Peer全表示かつ1ページ目にいる場合
          return this.carousel.current == 1
        } else {
          // ホワイトボード表示時、もしくは自分が画面共有している場合
          return !this.screenSharerPeerId || this.myUserId == this.screenSharerPeerId
        }
      } else {
        // PC表示で、1ページ目にいる、もしくは自分が画面共有している場合
        return this.carousel.current == 1 || this.myUserId == this.screenSharerPeerId
      }
    },
    isDisplayingScreenSharerPeerView: function() {
      return this.screenSharerPeerId && this.peers[this.screenSharerPeerId]
    },
    isDisplayingCarousel: function() {
      return !this.$deviceInfo.isMobile || this.viewMode !== 'whiteboard'
    }
  },
  methods: {
    ...mapActions({
      setPerPages: 'roomPeers/setPerPages',
      debugSetPeer: 'roomPeers/setPeer',
      changeCarousel: 'roomPeers/changeCarousel'
    })
  },
  async created() {
    await this.setPerPages(this.$deviceInfo.isMobile)
  },
  async mounted() {
    // DEBUG: デバッグ用Peers
    // this.debugSetPeer(Array.from({length: 20}, (v, k) => k).reduce((result, current, index) => {
    //   result[`debug${index}`] = {
    //     displayName: `peer${current}`
    //   }
    //   return result
    // }, {}))
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.peers-container-wrapper {
  width: 100%;
  height: 100%;
  position: relative;

  &.fluid .peer-view {
    width: 100%;
    height: 210px;

    @include mq(md) {
      width: 33.33%;
      height: 33.33%;
    }

    &:nth-child(2n) {
      top: 210px !important;

      @include mq(md) {
        top: 0 !important;
      }
    }

    &:first-of-type:nth-last-of-type(2),
    &:first-of-type:nth-last-of-type(2) ~ .peer-view,
    &:first-of-type:nth-last-of-type(3),
    &:first-of-type:nth-last-of-type(3) ~ .peer-view,
    &:first-of-type:nth-last-of-type(4),
    &:first-of-type:nth-last-of-type(4) ~ .peer-view {
      position: absolute;
      top: 0;
      width: 100%;
      height: 210px;

      @include mq(md) {
        position: relative;
        width: 50%;
        height: 50%;
      }
    }
    &:first-of-type:nth-last-of-type(5),
    &:first-of-type:nth-last-of-type(5) ~ .peer-view {
      &:nth-of-type(-n + 2) {
        position: absolute;
        top: 0;
        width: 100%;
        height: 210px;

        @include mq(md) {
          position: relative;
          width: 50%;
          height: 50%;
        }
      }
    }
  }

  &.side-pain .peer-view {
    visibility: hidden;

    @include mq(md) {
      visibility: visible;
    }

    width: 50%;
    height: 50%;

    &:first-of-type:nth-last-of-type(2),
    &:first-of-type:nth-last-of-type(2) ~ .peer-view {
      width: 50.1%;
      height: 50%;
    }

    &:first-of-type:nth-last-of-type(5),
    &:first-of-type:nth-last-of-type(5) ~ .peer-view,
    &:first-of-type:nth-last-of-type(6),
    &:first-of-type:nth-last-of-type(6) ~ .peer-view {
      width: 33.4%;
      height: 33.33%;
    }

    &:first-of-type:nth-last-of-type(7),
    &:first-of-type:nth-last-of-type(7) ~ .peer-view,
    &:first-of-type:nth-last-of-type(8),
    &:first-of-type:nth-last-of-type(8) ~ .peer-view {
      width: 33.33%;
      height: 33.33%;
    }
  }

  &.whiteboard {
    width: 60%;
    position: absolute;

    @include mq(md) {
      width: 10%;
      position: relative;
    }

    .peer-view {
      width: 100%;

      &:before {
        content: "";
        display: block;
        padding-top: 66.66%;
      }
    }
  }

  &.invisible {
    width: 0%;
    visibility: hidden;
  }
}

.peers-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: baseline;
  flex-wrap: wrap;

  @include mq(md) {
    align-content: center;
  }
}
</style>
